/**
 * ============================================================
 * Kidultverse® — Custom Mobile Navigation JS
 * File: assets/nav-mobile-kidultverse.js
 * Compatible with: Kalles v4.3.2 (The4 theme)
 *
 * Behaviour:
 *  - Slide-stack: tapping a parent item slides in the child
 *    panel from the right; Back button slides it out.
 *  - Body scroll is locked while the menu is open.
 *  - Swipe-right on the panel closes / goes back.
 *  - Keyboard: Escape closes; Tab stays inside the panel.
 * ============================================================
 */

(function () {
  'use strict';

  /* ── State ─────────────────────────────────────────────── */
  const stack = [];        // history of panel IDs
  let isOpen  = false;

  /* ── Element refs ───────────────────────────────────────── */
  const menu      = document.getElementById('kv-mobile-menu');
  const panel     = document.getElementById('kv-menu-panel');
  const backdrop  = document.getElementById('kv-menu-backdrop');
  const closeBtn  = document.getElementById('kv-menu-close');
  const backBtn   = document.getElementById('kv-menu-back');
  const breadcrumb= document.getElementById('kv-breadcrumb');
  const breadLabel= document.getElementById('kv-breadcrumb-label');

  if (!menu) return; // snippet not present on this page

  /* ── Open / close ───────────────────────────────────────── */
  function openMenu() {
    isOpen = true;
    stack.length = 0;
    stack.push('kv-panel-root');

    // Activate root panel
    document.querySelectorAll('.kv-panel').forEach(p => {
      p.classList.remove('kv-panel--active', 'kv-panel--exit');
    });
    const root = document.getElementById('kv-panel-root');
    if (root) root.classList.add('kv-panel--active');

    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    // Class-based lock so a CSS rule can target whatever Kalles/T4S actually scrolls
    document.documentElement.classList.add('kv-no-scroll');
    updateBreadcrumb();

    // Focus first focusable element
    requestAnimationFrame(() => {
      const first = panel.querySelector('a, button, input');
      if (first) first.focus();
    });
  }

  function closeMenu() {
    isOpen = false;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('kv-no-scroll');

    // Restore focus to the trigger that opened the menu
    if (window._kvTrigger) window._kvTrigger.focus();
  }

  /* ── Navigate to child panel ────────────────────────────── */
  function pushPanel(targetId) {
    const current = document.getElementById(stack[stack.length - 1]);
    const next    = document.getElementById(targetId);
    if (!current || !next) return;

    // Slide current panel out to the left
    current.classList.remove('kv-panel--active');
    current.classList.add('kv-panel--exit');

    // Slide next panel in from the right
    next.classList.add('kv-panel--active');
    next.scrollTop = 0;

    stack.push(targetId);
    updateBreadcrumb();

    // Focus first item in new panel
    requestAnimationFrame(() => {
      const first = next.querySelector('a, button');
      if (first) first.focus();
    });
  }

  /* ── Navigate back ──────────────────────────────────────── */
  function popPanel() {
    if (stack.length <= 1) { closeMenu(); return; }

    const current = document.getElementById(stack.pop());
    const prev    = document.getElementById(stack[stack.length - 1]);
    if (!current || !prev) return;

    // Slide current panel out to the right
    current.classList.remove('kv-panel--active');
    // Remove exit class from previous panel
    prev.classList.remove('kv-panel--exit');
    prev.classList.add('kv-panel--active');

    updateBreadcrumb();

    // Focus back button or first item
    requestAnimationFrame(() => {
      if (stack.length > 1) {
        backBtn.focus();
      } else {
        const first = prev.querySelector('a, button');
        if (first) first.focus();
      }
    });
  }

  /* ── Breadcrumb label ───────────────────────────────────── */
  function updateBreadcrumb() {
    if (stack.length <= 1) {
      breadcrumb.style.display = 'none';
    } else {
      breadcrumb.style.display = 'block';
      // Get the title of the current panel's parent
      const parentId = stack[stack.length - 2];
      const parentPanel = document.getElementById(parentId);
      const parentTitle = parentPanel ? parentPanel.getAttribute('data-title') : 'Back';
      breadLabel.textContent = parentTitle;
    }
  }

  /* ── Event listeners ────────────────────────────────────── */

  // Close button
  closeBtn.addEventListener('click', closeMenu);

  // Backdrop click
  backdrop.addEventListener('click', closeMenu);

  // Back button
  backBtn.addEventListener('click', popPanel);

  // Parent items → push panel
  menu.addEventListener('click', function (e) {
    const item = e.target.closest('.kv-item--parent');
    if (!item) return;
    const targetId = item.getAttribute('data-target');
    if (targetId) pushPanel(targetId);
  });

  // Keyboard: Escape closes; Tab traps inside panel
  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      if (stack.length > 1) popPanel();
      else closeMenu();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ── Swipe-right to close / go back ─────────────────────── */
  let touchStartX = 0;
  let touchStartY = 0;

  panel.addEventListener('touchstart', function (e) {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  panel.addEventListener('touchend', function (e) {
    const dx = e.changedTouches[0].clientX - touchStartX;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
    // Left-anchored drawer: swipe LEFT > 60px (toward the edge) goes back / closes
    if (dx < -60 && dy < 40) {
      if (stack.length > 1) popPanel();
      else closeMenu();
    }
  }, { passive: true });

  /* ── Public API — wire up trigger buttons ───────────────── */
  // Any element with [data-kv-open] will open the menu.
  // Example: <button data-kv-open aria-label="Open menu">☰</button>
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-kv-open]');
    if (trigger) {
      e.preventDefault(); // trigger is an <a href> — stop navigation, just open the drawer
      window._kvTrigger = trigger;
      openMenu();
    }
  });

  // Also support the legacy Kalles hamburger class if needed:
  // document.querySelector('.header__hamburger')?.addEventListener('click', openMenu);

  /* ── Expose for external use ────────────────────────────── */
  window.KvMenu = { open: openMenu, close: closeMenu };

})();
